import axios from 'axios';
import { Visit, VisitBasic, Snackbar } from './models';

const service = axios.create({
		baseURL: '/api/ajax/AppointmentsApi/'
	}),
	ratingService = axios.create({
		baseURL: '/api/ajax/RatingApi/'
	});

const MIN_YEAR_PREVIOUS_VISITS = 2000;

export default {
	/**
	 * Cancel appointment by siteId and appointmentId. Will fail if cancellation conditions are not met (configured by the salon).
	 * @param {Object} params
	 * @param {Number} params.siteId - The siteId the appointment belongs to.
	 * @param {String} params.appointmentId - The appointment's ID.
	 * @returns True/false whether cancellation was successful. If transaction fails due to cancellation policies, it returns an object with the error.
	 */
	async CancelAppointment({ siteId = 0, appointmentId = '' }) {
		const result = await axios.post('/api/ajax/AppointmentsApi/CancelAppointment', { siteId: siteId, AppointmentId: appointmentId });
		switch (Object(result.data).ResponseCode) {
			case 200:
			case 202:
			case 703:
				return true;

			case 208:
				return new Snackbar({ text: 'Avbestillingsfristen er nå passert. Vennligst ta kontakt med salongen direkte.', type: 'error' });

			default:
				return new Snackbar({ text: 'Klarte ikke å avbestille timen. Prøv på nytt eller kontakt salongen direkte.', type: 'error' });
		}
	},

	/**
	 * Get future visits/appointments for current logged in user.
	 * @returns {Visit[]}
	 */
	async GetFutureVisits(params) {
		const result = await axios.post('/api/ajax/AppointmentsApi/GetFutureAppointments', params);
		if (result.data && result.data.ResponseCode === 200) {
			return result.data.FutureVisits.map(visit => new Visit(visit));
		}

		return [];
	},

	/**
	 * Get historical visits/appointments for current logged in user.
	 * @returns {Visit[]}
	 */
	async GetHistoricalVisits(params) {
		const result = await axios.post('/api/ajax/AppointmentsApi/GetPreviousAppointments', Object.assign(params, {
			yearFrom: MIN_YEAR_PREVIOUS_VISITS,
			yearTo: new Date().getFullYear()
		}));
		if ( result.data && result.data.ResponseCode === 200 ) {
			return result.data.HistoricVisits.map(visit => new Visit(visit));
		}

		return [];
	},

	async GetBasicHistoricalVisits(params) {
		const result = await axios.post('/api/ajax/AppointmentsApi/GetBasicPreviousAppointments', Object.assign(params, {
			yearFrom: MIN_YEAR_PREVIOUS_VISITS,
			yearTo: new Date().getFullYear()
		}));
		if (result.data && result.data.ResponseCode === 200) {
			return result.data.HistoricVisits.map(visit => new VisitBasic(visit));
		}
		return [];
	},

	async GetDetailedHistoricalVisits(id) {
		const result = await axios.post('/api/ajax/AppointmentsApi/GetDetailedPreviousAppointments', {
			istpn: false,
			chainId: 0,
			visitId: id
		});
		if (result.data && result.data.ResponseCode === 200) {
			return result.data.HistoricVisits.map(visit => new Visit(visit));
		}
		return [];
	},

	/**
	 * Submit and send rating for provided visit/appointment.
	 * @param {Object} params
	 * @param {Number} params.rating - The rating given.
	 * @param {String} params.ratingComment - The comment attached to the rating.
	 * @param {Boolean} params.isPublic - If the rating should be publically visible to all on Fixit.no
	 * @param {Visit} params.visit - The visit the rating is applied for.
	 */
	async SendRating({ rating = 0, ratingComment = '', isPublic = true, visit = new Visit() }) {
		const result = await axios.post('/api/ajax/RatingApi/SubmitRating', {
			rating,
			comment: ratingComment,
			isPublic,
			besokId: visit.visitId,
			customerId: visit.customerId,
			siteId: visit.siteId
		});
		switch (Object(result.data).ResponseCode) {
			case 200:
				return true;

			default:
				return false;
		}
	}
};
